import { LogLevel } from '../constants/log.constants';

import { BaseMessageRequest, BaseMessageProgress, BaseMessageResult } from '../interfaces/messaging.interface';

import { Logger } from './logger';

export interface ISpinner {
  ensureIsVisible: () => Promise<void>,
  hideAll: () => Promise<void>;
  setMessage: (title?: string, step?: string, subStep?: string) => void;
}

export class SyncOperationHandler {

  private static readonly ProgressTimeoutMs = 30000;

  private logger: Logger;
  private progressTimeoutId: number;
  private lastOperationName: string;
  private lastProgressMessage: string;

  constructor(
    logLevel: LogLevel,
    private requestSource: string,
    private spinner: ISpinner,
    private onError: <TResult extends BaseMessageResult>(operationName: string, result: TResult) => void) {

    const loggerName = `${requestSource}: ${this.constructor.name}`;
    this.logger = new Logger(loggerName, logLevel);
  }

  async onStarted<TRequest extends BaseMessageRequest>(
    operationName: string,
    request: TRequest): Promise<void> {

    const logPrefix = `onStarted: ${operationName}`;
    this.logger.log(`${logPrefix}: `, { request });

    if (request.requestSource !== this.requestSource) {
      return;
    }

    this.logger.debug(`${logPrefix}: showing spinner`);
    await this.spinner.ensureIsVisible();

    this.lastOperationName = operationName;
    this.lastProgressMessage = null;
    this.clearProgressTimeout();
    this.setProgressTimeout();
  }

  async onProgress<TRequest extends BaseMessageRequest, TProgress extends BaseMessageProgress>(
    operationName: string,
    request: TRequest,
    progress: TProgress): Promise<void> {

    const logPrefix = `onProgress: ${operationName}`;
    this.logger.log(`${logPrefix}: `, { request, progress });

    if (progress.logLevel !== LogLevel.info) {
      return;
    }

    if (request.requestSource !== this.requestSource) {
      return;
    }

    this.logger.debug(`${logPrefix}: showing spinner`);
    await this.spinner.ensureIsVisible();

    this.spinner.setMessage(operationName, progress.message, progress.details);

    this.lastOperationName = operationName;
    this.lastProgressMessage = `${progress.message} ${progress.details ?? ''}`;
    this.clearProgressTimeout();
    this.setProgressTimeout();
  }

  async onEnded<TRequest extends BaseMessageRequest, TResult extends BaseMessageResult>(
    operationName: string,
    request: TRequest,
    result: TResult): Promise<void> {

    const logPrefix = `onEnded: ${operationName}`;
    this.logger.log(`${logPrefix}: `, { request, result });

    if (request.requestSource !== this.requestSource) {
      return;
    }

    this.logger.debug(`${logPrefix}: hiding spinner`);
    await this.spinner.hideAll();

    this.clearProgressTimeout();

    if (result.error) {
      this.onError(operationName, result);
    }
  }

  private setProgressTimeout(): void {
    const logPrefix = 'setProgressTimeout';
    this.progressTimeoutId = <number><unknown>setTimeout(
      () => {
        this.logger.warn(`${logPrefix}: hiding spinner due to timeout, operation: ${this.lastOperationName}, message: ${this.lastProgressMessage}`);
        this.spinner.hideAll();
      },
      SyncOperationHandler.ProgressTimeoutMs);
  }

  private clearProgressTimeout(): void {
    clearTimeout(this.progressTimeoutId);
  }
}
